import type { ISlackConnection, SlackConnectionStatus } from '@skystone/shared';
import { Badge, Button } from '@/components/ui';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { formatDateTime } from '@/lib/date-utils';

interface SlackConnectionCardProps {
  connection: ISlackConnection | null;
  status: SlackConnectionStatus;
  badge: { label: string; variant: 'success' | 'warning' | 'secondary' };
  isConnected: boolean;
  verifying: boolean;
  disconnecting: boolean;
  onVerify: () => void;
  onDisconnect: () => void;
}

export function SlackConnectionCard({
  connection,
  status,
  badge,
  isConnected,
  verifying,
  disconnecting,
  onVerify,
  onDisconnect,
}: Readonly<SlackConnectionCardProps>) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <CardTitle className="text-lg">Connection Status</CardTitle>
          <Badge variant={badge.variant}>{badge.label}</Badge>
        </div>
        <CardDescription>
          Manage the connection between Skystone and your Slack workspace.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {status === 'not_configured' && (
          <p className="text-sm text-muted-foreground">
            Set the <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">SLACK_BOT_TOKEN</code> environment variable and restart the service, then click Verify Connection.
          </p>
        )}

        {isConnected && connection && (
          <div className="mb-4 space-y-1 text-sm">
            <p>
              <span className="font-medium text-foreground">Workspace:</span>{' '}
              <span className="text-muted-foreground">{connection.workspaceName}</span>
            </p>
            <p>
              <span className="font-medium text-foreground">Workspace ID:</span>{' '}
              <span className="text-muted-foreground">{connection.workspaceId}</span>
            </p>
            {connection.botUserId && (
              <p>
                <span className="font-medium text-foreground">Bot User ID:</span>{' '}
                <span className="text-muted-foreground">{connection.botUserId}</span>
              </p>
            )}
            {connection.connectedAt && (
              <p>
                <span className="font-medium text-foreground">Connected At:</span>{' '}
                <span className="text-muted-foreground">
                  {formatDateTime(connection.connectedAt)}
                </span>
              </p>
            )}
          </div>
        )}

        {status === 'disconnected' && (
          <p className="mb-4 text-sm text-muted-foreground">
            The Slack connection was previously active but has been disconnected. Click Verify Connection to reconnect.
          </p>
        )}

        <div className="flex gap-3">
          <Button
            variant="primary"
            onClick={onVerify}
            isLoading={verifying}
            disabled={verifying || disconnecting}
          >
            Verify Connection
          </Button>
          {isConnected && (
            <Button
              variant="destructive"
              onClick={onDisconnect}
              isLoading={disconnecting}
              disabled={verifying || disconnecting}
            >
              Disconnect
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
